import { IDB_posts } from '../IDB/IDB.dexie'
import { Post_createPostId, type Post_TPost, type Post_TPostId, type Post_TReportPost } from './Post.TPost'
import type { Rpts_ClientId } from '../Rpts/Rpts.types'
import type { Type_Workflow } from '../Type/Type.hive'
import { User_get } from '../User/User.get'
import type { Nullable, UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'

interface ReportPostArgs {
    postId?: Post_TPostId
    reportClientId: Rpts_ClientId
    reportParentClientId?: Rpts_ClientId
    locationId: number
    procedure: Nullable<{ id: number }>
    workflow: Pick<Type_Workflow, 'name' | 'id'>
    title?: string
    startDate: Date
    stepsCompleted: number
    reportStatusId?: number
    subjectUnitId?: number
    inProgress: boolean
}

/**
 * Post.reportPostCreate
 *
 * Creates a new reportPost from an open report and saves it to indexed db.
 */
export async function Post_reportPostCreate(logger: ClientLogger, args: ReportPostArgs): Promise<Post_TPost> {
    try {
        logger.log(`saving report post`, { reportClientId: args.reportClientId })

        const post: Post_TPost = {
            id: args.postId ?? Post_createPostId(),
            procedure: args.procedure?.id ?? -1,
            workflow: args.workflow?.id ?? -1,
            location: args.locationId ?? null,
            unit: args.subjectUnitId ?? -1,
            createdAt: args.startDate,
            createdBy: User_get().id,
            lastModifiedAt: args.startDate,
            lastModifiedBy: User_get().id,
            lastModifiedType: 'created',
            serverSyncTime: new Date(),
            isDeleted: false,
            commentCount: 0,
            status: 'pendingReportUpload',
            contentType: 'report',
            reportClientId: args.reportClientId,
            reportActivities: args.stepsCompleted,
            reportWorkflowName: args.workflow?.name,
            reportStatus: args.reportStatusId,
            reportWorkflowId: args.workflow?.id,
            inProgress: args.inProgress,
            reportIsConfidential: false,
        }

        if (args.reportParentClientId != null) {
            post.reportParentClientId = args.reportParentClientId
        }

        // Optionally add report title
        if (args.title != null) {
            post.reportTitle = args.title
        }

        await IDB_posts().add(post)

        logger.log(`saved report post`, { reportClientId: args.reportClientId, postId: post.id })

        return post
    } catch (error) {
        logger.error(error)
    }
}

export async function Post_readReportPost(logger: ClientLogger, reportClientId: UUID) {
    const post = await IDB_posts()
        .where({ reportClientId })
        .filter((p) => p.contentType === 'report')
        .first()

    if (post == null) {
        logger.warn(`report post not found`, { reportClientId })
    }

    return post
}

/**
 * Does not fail if the post does not exist
 */
async function modifyReportPost(reportClientId: ReportClientId, fn: (post: Post_TReportPost) => void) {
    await IDB_posts()
        .where({ reportClientId })
        .modify((post) => {
            if (post.contentType !== 'report') return
            fn(post)
        })
}

export async function Post_setReportPostInProgress(reportClientId: ReportClientId, inProgress: boolean) {
    await modifyReportPost(reportClientId, (post) => (post.inProgress = inProgress))
}

export async function Post_setReportPostTitle(reportClientId: ReportClientId, reportTitle: string) {
    await modifyReportPost(reportClientId, (post) => (post.reportTitle = reportTitle))
}

export async function Post_setReportPostSubjectUnit(reportClientId: ReportClientId, subjectUnitId: number) {
    await modifyReportPost(reportClientId, (post) => (post.unit = subjectUnitId))
}

export async function Post_setReportPostStepsCount(reportClientId: ReportClientId, stepsCount: number) {
    await modifyReportPost(reportClientId, (post) => (post.reportActivities = stepsCount))
}

export async function Post_setReportPostStatus(reportClientId: ReportClientId, reportStatusId: number) {
    await modifyReportPost(reportClientId, (post) => (post.reportStatus = reportStatusId))
}
